/**
 * Monitoramento da interface com o Sentry (https://sentry.io): os erros (do código, do React e as
 * respostas 5xx do servidor), o desempenho (carregamento, chamadas da API, Web Vitals, travadas),
 * gravações da sessão (replay), o console como logs e, se ligado, o perfil do JavaScript.
 *
 * Liga só com VITE_SENTRY_DSN no build (README, "Monitoramento com o Sentry"); sem ela nada é
 * enviado. O main.tsx importa este arquivo antes de tudo, para pegar até os erros do carregamento.
 *
 * Privacidade: o replay cobre textos, campos e imagens (o padrão do Sentry), os corpos das chamadas
 * não vão, e cabeçalhos ou parâmetros com cara de segredo (`authorization`, `token`) vão filtrados.
 */
import * as Sentry from '@sentry/react';
import type { RootOptions } from 'react-dom/client';
import { SERVER_BASE } from './serverUrl';

/** As variáveis do build que este arquivo lê (todas opcionais menos MODE). */
export type MonitoringEnv = {
  MODE: string;
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
  VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE?: string;
  VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE?: string;
  VITE_SENTRY_PROFILES_SAMPLE_RATE?: string;
};

export type MonitoringConfig = {
  dsn: string;
  environment: string;
  /** fração das sessões com desempenho medido */
  tracesSampleRate: number;
  /** fração das sessões gravadas inteiras */
  replaysSessionSampleRate: number;
  /** fração das sessões com erro cuja gravação (o minuto antes do erro) vai junto */
  replaysOnErrorSampleRate: number;
  /** fração das sessões com o perfil do JavaScript (só Chrome e Edge) */
  profileSessionSampleRate: number;
};

/** A configuração do Sentry tirada das variáveis do build; `null` = desligado (sem DSN). */
export function monitoringConfig(env: MonitoringEnv): MonitoringConfig | null {
  const dsn = env.VITE_SENTRY_DSN?.trim();
  if (!dsn) return null;
  return {
    dsn,
    environment: env.VITE_SENTRY_ENVIRONMENT?.trim() || env.MODE,
    tracesSampleRate: rate(env.VITE_SENTRY_TRACES_SAMPLE_RATE, 1),
    replaysSessionSampleRate: rate(env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0.1),
    replaysOnErrorSampleRate: rate(env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1),
    profileSessionSampleRate: rate(env.VITE_SENTRY_PROFILES_SAMPLE_RATE, 0),
  };
}

/** Uma fração de 0 a 1; vazio ou inválido = o padrão. */
function rate(value: string | undefined, fallback: number): number {
  if (value === undefined || value.trim() === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : fallback;
}

/**
 * Para onde as chamadas levam o rastro (`sentry-trace` e `baggage`), que o servidor continua
 * (apps/server/src/monitoring.ts): o mesmo endereço da página e o servidor à parte, se houver. O
 * CORS do servidor aceita os dois cabeçalhos; nenhum outro endereço os recebe.
 */
export function tracePropagationTargets(serverBase: string): RegExp[] {
  const sameOrigin = /^\/(?!\/)/;
  if (!serverBase) return [sameOrigin];
  const origin = new URL(serverBase).origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return [sameOrigin, new RegExp(`^${origin}/`)];
}

const config = monitoringConfig(import.meta.env);

if (config) {
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    // a versão (o commit) vem do plugin do Sentry no build, junto com os source maps (vite.config.ts)
    tracesSampleRate: config.tracesSampleRate,
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    profileSessionSampleRate: config.profileSessionSampleRate,
    profileLifecycle: 'trace',
    tracePropagationTargets: tracePropagationTargets(SERVER_BASE),
    dataCollection: { httpBodies: [] },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'info', 'warn', 'error'] }),
      // respostas 5xx do servidor viram erros (as 4xx são respostas normais: senha errada, sem acesso)
      Sentry.httpClientIntegration(),
      ...(config.profileSessionSampleRate > 0 ? [Sentry.browserProfilingIntegration()] : []),
    ],
    ignoreErrors: [
      // aviso inofensivo do navegador quando um ResizeObserver muda o próprio tamanho
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });
}

/** O monitoramento está ligado nesta página. */
export const MONITORING = config !== null;

/**
 * Os ganchos de erro da raiz do React: o Sentry recebe o erro com a pilha dos componentes, e o
 * console continua mostrando (e o registro de erros de Configurações → Solução de problemas, guardando).
 * Desligado, fica o comportamento padrão do React.
 */
export function reactRootOptions(): RootOptions {
  if (!config) return {};
  const keepInConsole = (error: unknown) => console.error(error);
  return {
    onUncaughtError: Sentry.reactErrorHandler(keepInConsole),
    onCaughtError: Sentry.reactErrorHandler(keepInConsole),
    onRecoverableError: Sentry.reactErrorHandler(keepInConsole),
  };
}

/** Quem está usando e onde: vai junto de cada erro (o id e o nome; o e-mail não). */
export function setMonitoringUser(user: { id: string; displayName: string } | null): void {
  Sentry.setUser(user ? { id: user.id, username: user.displayName } : null);
}

export function setMonitoringRoom(room: { id: string; name: string } | null): void {
  Sentry.setTag('room', room?.id);
  Sentry.setContext('room', room);
}

/** Um passo no caminho até o erro (conexão, avisos na tela). */
export function note(category: string, message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  Sentry.addBreadcrumb({ category, message, level });
}

/** Algo errado que não estourou como exceção (o servidor mandou algo fora do protocolo, por exemplo). */
export function reportProblem(message: string): void {
  Sentry.captureMessage(message, 'warning');
}
